
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Check, Download } from 'lucide-react';
import { Label } from '../ui/label';
import { DocumentType } from '../../types/dataRoom';
import { formatDocumentType } from '../../utils/documentUtils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

interface DocumentDialogsProps {
  isEditTagsOpen: boolean;
  setIsEditTagsOpen: (open: boolean) => void;
  currentTags: string;
  setCurrentTags: (tags: string) => void;
  handleSaveTags: () => void;
  
  isEditTypeOpen: boolean;
  setIsEditTypeOpen: (open: boolean) => void;
  currentType: DocumentType;
  setCurrentType: (type: DocumentType) => void;
  handleSaveType: () => void;
  documentTypeOptions: DocumentType[];
  
  isViewDocumentOpen: boolean;
  viewDocumentUrl: string;
  viewDocumentName: string;
  closeViewDocument: () => void;
}

const DocumentDialogs: React.FC<DocumentDialogsProps> = ({
  isEditTagsOpen,
  setIsEditTagsOpen,
  currentTags,
  setCurrentTags,
  handleSaveTags,
  
  isEditTypeOpen,
  setIsEditTypeOpen,
  currentType,
  setCurrentType,
  handleSaveType,
  documentTypeOptions,
  
  isViewDocumentOpen,
  viewDocumentUrl,
  viewDocumentName,
  closeViewDocument,
}) => {
  return (
    <>
      {/* Edit Tags Dialog */}
      <Dialog open={isEditTagsOpen} onOpenChange={setIsEditTagsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Document Tags</DialogTitle>
            <DialogDescription>
              Enter tags separated by commas to categorize this document.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="tags">
              Tags
            </Label>
            <Input
              id="tags"
              value={currentTags}
              onChange={(e) => setCurrentTags(e.target.value)}
              className="mt-1"
              placeholder="e.g. important, tax, 2023"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditTagsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTags}>
              <Check className="mr-2 h-4 w-4" />
              Save Tags
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Document Type Dialog */}
      <Dialog open={isEditTypeOpen} onOpenChange={setIsEditTypeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Document Type</DialogTitle>
            <DialogDescription>
              Select a different document type to recategorize this document.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="document-type">
              Document Type
            </Label>
            <Select
              value={currentType}
              onValueChange={(value) => setCurrentType(value as DocumentType)}
            >
              <SelectTrigger className="w-full mt-1">
                <SelectValue placeholder="Select document type" />
              </SelectTrigger>
              <SelectContent>
                {documentTypeOptions.map((type) => (
                  <SelectItem key={type} value={type}>
                    {formatDocumentType(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditTypeOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveType}>
              <Check className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Document Dialog */}
      <Dialog open={isViewDocumentOpen} onOpenChange={closeViewDocument}>
        <DialogContent className="sm:max-w-[90%] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{viewDocumentName}</DialogTitle>
          </DialogHeader>
          <div className="py-4 h-[70vh] overflow-auto">
            {viewDocumentUrl && (
              <iframe 
                src={viewDocumentUrl} 
                width="100%"
                height="600px"
                className="w-full h-full border-0" 
                title={viewDocumentName}
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeViewDocument}>
              Close
            </Button>
            <Button 
              onClick={() => {
                const a = document.createElement('a');
                a.href = viewDocumentUrl;
                a.download = viewDocumentName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentDialogs;
